export default defineAppConfig({
	/**
	 * How category and manufacturer listings page through products.
	 * `numbered` — page links (NF-280); `loadMore` — a "show more" button that
	 * appends pages and keeps `?p` in sync. A template overrides it in its own
	 * `app.config.ts`. Read it through `useProductListingPagination()`.
	 */
	productListing: {
		pagination: "numbered"
	},
	/**
	 * `specificationCodes` — the parametric codes the PDP specification tab may
	 * show. `null` shows every visible parametric; a template that keeps working
	 * data in parametrics lists the customer-facing ones instead.
	 * `hiddenSpecifications` — parametrics the tab never shows, by name or code,
	 * for a template whose spec is mostly customer-facing but carries a few that
	 * only drive other UI (colour swatches, filters). The default only: the
	 * product page layout's `hidden_specifications` field replaces it when set.
	 * `unlabelledSpecifications` — parametric names whose row shows only the
	 * value, full width: a tab already titled by the parametric.
	 * `deliveryAndReturnsTab` — the tab that shows the product's
	 * `SubDescription`; off for a template that shows it elsewhere.
	 * `lowestPriceBeforeReduction` — show the lowest-price note only on a
	 * reduction, with the price before it, instead of the plain 30-day low.
	 * `lowestPriceAlways` — show the note on every product: the 30-day low, or
	 * today's price when it has not changed, where there is no other figure.
	 * `packThumbSize` — the CSS size (px) a template draws the multi pack
	 * thumbnails at, so they are fetched sharp at 2x; `null` keeps the
	 * picker's own size.
	 * `rating` — the Lipscore rating and a reviews link beside the price.
	 * `categoryBreadcrumb` — PDP breadcrumbs from the product's primary
	 * category, also on a direct visit.
	 * `storeAvailabilityLink` — the "see availability in stores" link.
	 * `highlights` — under the price, the sub header, part number and this
	 * many `SubDescription` bullets instead of the short description; `null`
	 * keeps the short description.
	 * All are read through `useProductDetailsConfig()`.
	 */
	productDetails: {
		specificationCodes: null,
		hiddenSpecifications: [],
		unlabelledSpecifications: [],
		deliveryAndReturnsTab: true,
		lowestPriceBeforeReduction: false,
		lowestPriceAlways: false,
		packThumbSize: null,
		rating: false,
		categoryBreadcrumb: false,
		storeAvailabilityLink: true,
		highlights: null
	},
	/**
	 * `rating` — Lipscore stars on the base `ProductCard`; `subHeader` — the
	 * Norce `SubHeader` under the name. Read through `useProductCardConfig()`.
	 */
	productCard: {
		rating: false,
		subHeader: false
	},
	/**
	 * PDP files by Norce file code: `videoCodes` (description video) and
	 * `documentGroups` (`{ label, codes }` accordions). Only hogsta's PDP reads
	 * them so far. Arrays add up across layers, so base stays empty.
	 */
	productFiles: {
		videoCodes: [],
		documentGroups: []
	},
	/**
	 * `productCard` — Meilisearch product hits render as the listing's
	 * `ProductCard` (flags, old price and campaign as on a category), instead
	 * of the search's own tile; both are priced from the index. Read
	 * it through `useSearchConfig()`.
	 */
	search: {
		productCard: false
	},
	/**
	 * `teaserImageSize` — the size a blog list card asks the image service
	 * for; its proportion is the crop, so a template sets its card's frame.
	 * Read it through `useBlogConfig()`.
	 */
	blog: {
		teaserImageSize: { width: 800, height: 600 }
	},
	/**
	 * `languageSwitcher` — the language/currency selector in the desktop
	 * header and the mobile side menu. Read through `useHeaderConfig()`.
	 */
	header: {
		languageSwitcher: true
	},
	/** `drilldown` (each level on its own screen) or `accordion` (levels expand in place). */
	mobileMenu: {
		layout: "drilldown"
	},
	listingFilters: {
		/** Show the price filter inputs with thousands grouping ("1 799"). */
		groupPriceThousands: false,
		/** `sidebar` (accordions beside the grid) or `row` (dropdowns in the toolbar). */
		layout: "sidebar"
	},
	/**
	 * `landingPageLinks` — the Store Finder links each store that has a Norce
	 * "Unique URL" to its landing page (SU-28). The landing page is rendered by
	 * the base CMS page, so a template with its own CMS page turns this off,
	 * or the links would 404. Read through `useStoreFinderConfig()`.
	 */
	storeFinder: {
		landingPageLinks: true
	},
	/**
	 * `titleTag` — recommendation and similar-products title element (`span`,
	 * `h2`, `h3`); a heading uses the template's heading sizes. Read through
	 * `useProductRecommendationsConfig()`.
	 */
	productRecommendations: {
		titleTag: "span"
	}
});
